import React, { useState } from 'react';
import { GuideOpenBody, GuidePhoto } from '../../../../../interfaces/stay-guide';
import { CopyValueButton } from '../../../../copy-value/copy-value-button';
import { ImageViewer } from '../../../../gallery/image-viewer';
import { Presence } from '../../../../../motion';
import { StayKey, t } from '../../../../onboarding/stay/stay-strings';
import { AccessNotice } from './access-notice';
import { GuideH1 } from './guide-frame';
import { langOf, mapsHref } from './guide-format';
import {
  CodeValue,
  HostText,
  LargeValue,
  Muted,
  PhotoButton,
  Row,
  SecondaryAnchor,
  SectionHeading,
  Stack,
  StepItem,
  StepList,
  StepNumber,
  ValueLabel,
} from './guide.styled';

const L = 'en' as const;

/**
 * ARRIVAL, STEP BY STEP (plan S7.7) — six cards, 01 to 06.
 *
 *   01 Find the building   address, directions, "Open in Maps"
 *   02 Park                the space (large) + instructions
 *   03 Enter the building  the building code (large, mono) + copy + entry steps
 *   04 Find your door      "Floor 3 · Door 12B" + directions
 *   05 Collect the key     the lockbox code + copy + instructions
 *   06 Get connected       network + password, each with copy
 *
 * Steps 03-06 exist ONLY when the server released `access`; until then the
 * access notice stands in their place (no empty cards, no placeholders that
 * name a code). A blank detail says "Contact your host for this detail".
 *
 * A step's photo opens in the viewer with the step's title as its alt text —
 * never the photo's own title, which a host could have typed a code into.
 */

/** A step photo: tap to see it whole. */
const StepPhoto: React.FC<{ photo: GuidePhoto | null; title: string }> = ({ photo, title }) => {
  const [open, setOpen] = useState(false);
  if (!photo) return null;

  return (
    <>
      <PhotoButton type="button" onClick={() => setOpen(true)} aria-label={t(L, 'guide.openPhoto', { title })}>
        <img src={photo.url} alt="" />
      </PhotoButton>
      <Presence>
        {open ? (
          <ImageViewer
            key={photo.id}
            images={[photo]}
            startIndex={0}
            altOf={() => title}
            onClose={() => setOpen(false)}
          />
        ) : null}
      </Presence>
    </>
  );
};

const Step: React.FC<{
  n: number;
  titleKey: StayKey;
  photo?: GuidePhoto | null;
  children: React.ReactNode;
}> = ({ n, titleKey, photo = null, children }) => {
  const title = t(L, titleKey);
  const id = `guide-step-${n}`;

  return (
    <StepItem aria-labelledby={id} data-guide-step={n}>
      <SectionHeading id={id}>
        <StepNumber>{String(n).padStart(2, '0')}</StepNumber> {title}
      </SectionHeading>
      <StepPhoto photo={photo} title={title} />
      {children}
    </StepItem>
  );
};

const ContactHost: React.FC = () => <Muted data-guide-blank="">{t(L, 'guide.contactHost')}</Muted>;

/** A labelled code with its copy button, or the blank line. */
const CodeBlock: React.FC<{ label: StayKey; value: string; copy: StayKey; copied: StayKey }> = ({
  label,
  value,
  copy,
  copied,
}) =>
  value ? (
    <Stack>
      <ValueLabel>{t(L, label)}</ValueLabel>
      <CodeValue>{value}</CodeValue>
      <CopyValueButton value={value} label={t(L, copy)} announce={t(L, copied)} />
    </Stack>
  ) : (
    <ContactHost />
  );

export const doorLineOf = (floor: string, door: string): string => {
  if (floor && door) return t(L, 'guide.doorLine', { floor, door });
  if (floor) return t(L, 'guide.floorOnly', { floor });
  if (door) return t(L, 'guide.doorOnly', { door });

  return '';
};

export const GuideArrival: React.FC<{ body: GuideOpenBody }> = ({ body }) => {
  const { guide, media, access } = body;
  const lang = langOf(guide.content_language);
  const maps = mapsHref(guide.maps_url, guide.address);
  const door = access ? doorLineOf(access.floor.trim(), access.apartment_door.trim()) : '';

  return (
    <>
      <GuideH1>
        {t(L, 'guide.arrivalTitle')}
      </GuideH1>
      <StepList>
        <Step n={1} titleKey="guide.step1" photo={media.step1}>
          {guide.address ? <LargeValue lang={lang}>{guide.address}</LargeValue> : <ContactHost />}
          {guide.directions ? <HostText lang={lang}>{guide.directions}</HostText> : null}
          {maps ? (
            <Row>
              <SecondaryAnchor href={maps} target="_blank" rel="noreferrer noopener">
                {t(L, 'guide.openInMaps')}
              </SecondaryAnchor>
            </Row>
          ) : null}
        </Step>

        <Step n={2} titleKey="guide.step2" photo={media.step2}>
          {guide.parking_space ? (
            <Stack>
              <ValueLabel>{t(L, 'guide.parkingSpaceLabel')}</ValueLabel>
              <LargeValue lang={lang}>{guide.parking_space}</LargeValue>
            </Stack>
          ) : (
            <ContactHost />
          )}
          {guide.parking_instructions ? <HostText lang={lang}>{guide.parking_instructions}</HostText> : null}
        </Step>

        {access ? (
          <>
            <Step n={3} titleKey="guide.step3" photo={access.media.step3}>
              <CodeBlock
                label="guide.buildingCodeLabel"
                value={access.building_code}
                copy="guide.copyBuildingCode"
                copied="guide.buildingCodeCopied"
              />
              {access.building_entry_steps ? <HostText lang={lang}>{access.building_entry_steps}</HostText> : null}
            </Step>

            <Step n={4} titleKey="guide.step4" photo={access.media.step4}>
              {door ? <LargeValue>{door}</LargeValue> : <ContactHost />}
              {access.apartment_directions ? <HostText lang={lang}>{access.apartment_directions}</HostText> : null}
            </Step>

            <Step n={5} titleKey="guide.step5" photo={access.media.step5}>
              <CodeBlock
                label="guide.lockboxCodeLabel"
                value={access.lockbox_code}
                copy="guide.copyLockboxCode"
                copied="guide.lockboxCodeCopied"
              />
              {access.lockbox_instructions ? <HostText lang={lang}>{access.lockbox_instructions}</HostText> : null}
            </Step>

            <Step n={6} titleKey="guide.step6" photo={access.media.step6}>
              {access.wifi_name || access.wifi_password ? (
                <>
                  {access.wifi_name ? (
                    <CodeBlock
                      label="guide.wifiNetwork"
                      value={access.wifi_name}
                      copy="guide.copyWifiName"
                      copied="guide.wifiNameCopied"
                    />
                  ) : null}
                  {access.wifi_password ? (
                    <CodeBlock
                      label="guide.wifiPassword"
                      value={access.wifi_password}
                      copy="guide.copyWifiPassword"
                      copied="guide.wifiPasswordCopied"
                    />
                  ) : null}
                </>
              ) : (
                <ContactHost />
              )}
            </Step>
          </>
        ) : (
          <li data-guide-step="notice">
            <AccessNotice notice={body.access_notice} zone={body.zone} />
          </li>
        )}
      </StepList>
    </>
  );
};
